import type { RyftAccountTransactionsListResponseItemType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import {
  Button,
  Card,
  CardContent,
  Icon,
  TooltipContainer,
  Spinner,
  TablePaginated,
  createSellarColumnHelper,
} from "@sellar-io/components";
import type { PaginationState } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Alert } from "~/components/alert/Alert";
import {
  Dialog,
  DialogContent,
  DialogNavigation,
} from "~/components/modal/Dialog";
import { ParentLink } from "~/components/navigation/ParentLink";
import { ORDER_DETAILS } from "~/constants/routes";
import { useRyftManagementLedgerBalance } from "~/hooks/api/v2/ryftManagement/useRyftManagementLedgerBalance";
import { useRyftManagementLedgerTransactions } from "~/hooks/api/v2/ryftManagement/useRyftManagementLedgerTransactions";
import { useRyftNonHostedDashboardAccount } from "./components/RyftNonHostedDashboardAccountProvider.shared";

type Transaction = RyftAccountTransactionsListResponseItemType;

const activityLabels: Record<Transaction["type"], string> = {
  capture: "Card payment",
  refund: "Refund for card payment",
  fundsTransfer: "Sellar Credit payment",
};

const amountLabels: Record<Transaction["type"], string | null> = {
  capture: "Before fees",
  refund: null,
  fundsTransfer: "After fees",
};

export const RyftNonHostedBalance = () => {
  const { account } = useRyftNonHostedDashboardAccount();

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <BalanceSummary />
      <TransactionHistory key={account.ryftAccountId} />
    </div>
  );
};

const BalanceSummary = () => {
  const query = useRyftManagementLedgerBalance();

  if (query.isPending) {
    return (
      <div
        className="flex min-h-28 items-center justify-center"
        role="status"
        aria-label="Loading balance"
      >
        <Spinner />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert type="error" message="Unable to load your balance." />
        <Button color="secondary" onClick={() => query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { balance, received, pending, currency } = query.data;
  return (
    <Card className="border border-zinc-200 bg-white">
      <CardContent className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 sm:grid-cols-3 sm:items-center md:px-6">
        <div className="col-span-2 sm:col-span-1">
          <h1 className="text-sm font-medium text-zinc-600">Your balance</h1>
          <p className="mt-1 text-3xl font-semibold text-zinc-900 tabular-nums">
            {formatCurrency(balance, currency)}
          </p>
        </div>
        <dl className="col-span-2 grid grid-cols-2 gap-4 border-t border-zinc-200 pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-6">
          <div>
            <dt className="flex items-center gap-1.5 text-sm font-medium text-zinc-600">
              Received
              <TooltipContainer content="Money we hold for you. Included in your balance.">
                <button
                  type="button"
                  aria-label="About received funds"
                  className="flex cursor-pointer rounded-sm text-zinc-400 hover:text-zinc-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-600"
                >
                  <Icon icon="INFORMATION_OUTLINE" size={0.6} />
                </button>
              </TooltipContainer>
            </dt>
            <dd className="mt-1 text-xl font-semibold text-zinc-900 tabular-nums">
              {formatCurrency(received, currency)}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-1.5 text-sm font-medium text-zinc-600">
              Pending
              <TooltipContainer content="Money on its way to your balance. Included in your balance.">
                <button
                  type="button"
                  aria-label="About pending funds"
                  className="flex cursor-pointer rounded-sm text-zinc-400 hover:text-zinc-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-600"
                >
                  <Icon icon="INFORMATION_OUTLINE" size={0.6} />
                </button>
              </TooltipContainer>
            </dt>
            <dd className="mt-1 text-xl font-semibold text-zinc-900 tabular-nums">
              {formatCurrency(pending, currency)}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
};

const TransactionHistory = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [selectedTransactionId, setSelectedTransactionId] = useState<number>();
  const query = useRyftManagementLedgerTransactions({
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
  });
  const selectedTransaction = query.data?.rows.find(
    (transaction) => transaction.id === selectedTransactionId,
  );
  const selectTransaction = (transaction: Transaction) =>
    setSelectedTransactionId(transaction.id);
  const columnHelper = createSellarColumnHelper<Transaction>();
  const columns = [
    columnHelper.accessor("effectiveAt", {
      header: "Date",
      enableSorting: false,
      cell: (info) => dayjs(info.getValue()).format("D MMM YYYY"),
    }),
    columnHelper.accessor("type", {
      header: "Type",
      enableSorting: false,
      cell: (info) => activityLabels[info.getValue()],
    }),
    columnHelper.accessor((row) => row, {
      id: "reference",
      header: "Reference",
      enableSorting: false,
      cell: (info) => (
        <TransactionReference
          transaction={info.getValue()}
          onSelect={selectTransaction}
        />
      ),
    }),
    columnHelper.accessor("amount", {
      header: "Amount",
      enableSorting: false,
      cell: (info) => {
        const amount = info.getValue();
        if (amount === null) {
          return null;
        }
        return (
          <div>
            <span
              className={twMerge(
                "tabular-nums",
                amount > 0 && "text-green-700",
                amount < 0 && "text-red-700",
              )}
            >
              {amount > 0 && "+"}
              {formatCurrency(amount, info.row.original.currency)}
            </span>
            {amountLabels[info.row.original.type] && (
              <span className="block text-xs text-zinc-500">
                {amountLabels[info.row.original.type]}
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "details",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <Button
          color="slim"
          size="small"
          aria-label={`View transaction ${info.getValue().id}`}
          onClick={() => selectTransaction(info.getValue())}
        >
          View
        </Button>
      ),
    }),
  ];

  return (
    <section
      className="flex min-w-0 flex-col gap-3"
      aria-labelledby="transaction-history-heading"
    >
      <h2
        id="transaction-history-heading"
        className="font-semibold text-zinc-900"
      >
        Transaction history
      </h2>
      {query.isPending && (
        <div
          className="flex min-h-48 items-center justify-center"
          role="status"
          aria-label="Loading transactions"
        >
          <Spinner />
        </div>
      )}
      {query.isError && (
        <div className="flex flex-col items-start gap-3">
          <Alert
            type="error"
            message="Unable to load your transaction history."
          />
          <Button color="secondary" onClick={() => query.refetch()}>
            Try again
          </Button>
        </div>
      )}
      {query.isSuccess && (
        <TablePaginated
          columns={columns}
          data={query.data.rows}
          pageCount={Math.max(
            1,
            Math.ceil(query.data.count / pagination.pageSize),
          )}
          pagination={{ ...pagination, isLoading: query.isFetching }}
          setPagination={setPagination}
          onRowClick={selectTransaction}
          paginationLabel="Transactions per page"
          noResultsText="Your payment activity will appear here."
          gridClasses="grid-cols-[110px_minmax(180px,1fr)_minmax(150px,1fr)_120px_88px]"
          roundedTop
        />
      )}
      <Dialog
        active={!!selectedTransaction}
        onDismiss={() => setSelectedTransactionId(undefined)}
      >
        <DialogNavigation
          title="Transaction details"
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setSelectedTransactionId(undefined)}
        />
        <DialogContent className="flex flex-col gap-5">
          {selectedTransaction && (
            <TransactionDetails transaction={selectedTransaction} />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const TransactionReference = ({
  transaction,
  onSelect,
}: {
  transaction: Transaction;
  onSelect?: (transaction: Transaction) => void;
}) => {
  if (transaction.orderId !== null) {
    return (
      <ParentLink
        to={ORDER_DETAILS.href({ orderId: transaction.orderId })}
        className="text-sm underline underline-offset-2"
      >
        Order #{transaction.orderId}
      </ParentLink>
    );
  }
  if (transaction.sellarCreditPayoutId !== null) {
    if (onSelect) {
      return (
        <button
          type="button"
          className="text-left text-sm underline underline-offset-2"
          onClick={() => onSelect(transaction)}
        >
          Credit payout #{transaction.sellarCreditPayoutId}
        </button>
      );
    }
    return <>Credit payout #{transaction.sellarCreditPayoutId}</>;
  }
  return <>—</>;
};

const TransactionDetails = ({ transaction }: { transaction: Transaction }) => {
  const details = [
    { label: "Type", value: activityLabels[transaction.type] },
    {
      label: "Date",
      value: dayjs(transaction.effectiveAt).format("D MMM YYYY, HH:mm"),
    },
    {
      label: "Reference",
      value: <TransactionReference transaction={transaction} />,
    },
    {
      label: "Amount",
      value: (
        <>
          {transaction.amount !== null &&
            formatCurrency(transaction.amount, transaction.currency)}
          {amountLabels[transaction.type] && (
            <span className="ml-2 text-sm text-zinc-500">
              {amountLabels[transaction.type]}
            </span>
          )}
        </>
      ),
    },
  ];
  if (transaction.type === "capture" && transaction.feeAmount !== null) {
    details.push({
      label: "Fee",
      value: <>{formatCurrency(transaction.feeAmount, transaction.currency)}</>,
    });
  }

  return (
    <dl className="divide-y divide-zinc-100">
      {details.map(({ label, value }) => (
        <div
          key={label}
          className="grid gap-1 py-3 sm:grid-cols-[160px_1fr] sm:gap-4"
        >
          <dt className="text-sm text-zinc-500">{label}</dt>
          <dd className="min-w-0 text-sm text-zinc-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
};
